/* eslint-disable class-methods-use-this */
import { DocumentDto, EntityDto, PagedResultDto } from '@services';
import http from '../httpService';
import {
  CreateInspection,
  CreateInspectionResponses,
  CreateInspectionTenant,
  Inspection,
  InspectionCategory,
  InspectionResponse,
  PagedInspectionRequest,
  UpdateInspection,
  UploadInspectionResponseDocument,
} from './inspection';

class InspectionService {
  public async getAll(request: PagedInspectionRequest): Promise<PagedResultDto<Inspection>> {
    const result = await http.get('api/services/app/Inspection/GetAll', {
      params: request,
    });
    return result.data.result;
  }

  public async get(entityDto: EntityDto<string>): Promise<Inspection> {
    const result = await http.get('api/services/app/Inspection/Get', { params: entityDto });
    return result.data.result;
  }

  public async getAllCategories(tenantId: number): Promise<InspectionCategory[]> {
    const result = await http.get(
      `api/services/app/Inspection/GetAllCategories?tenantId=${tenantId}`
    );
    return result.data.result;
  }

  public async getAllCategoriesByTenantId(tenantId: number): Promise<InspectionCategory[]> {
    const result = await http.get(
      `api/services/app/Inspection/GetAllCategoriesByTenantId?tenantId=${tenantId}`
    );
    return result.data.result;
  }

  public async create(request: CreateInspection): Promise<Inspection> {
    const result = await http.post('api/services/app/Inspection/Create', request);
    return result.data.result;
  }

  public async update(inspection: UpdateInspection): Promise<Inspection> {
    const result = await http.put('api/services/app/Inspection/Update', inspection);
    return result.data.result;
  }

  public async publish(id: EntityDto<string>): Promise<Inspection> {
    const result = await http.post('api/services/app/Inspection/Publish', id);
    return result.data.result;
  }

  public async createInspectionTenant(request: CreateInspectionTenant): Promise<void> {
    const result = await http.post('api/services/app/Inspection/CreateInspectionTenant', request);
    return result.data.result;
  }

  /** Returns only the items it saved, each with the row id the API settled on. */
  public async createResponses(request: CreateInspectionResponses): Promise<InspectionResponse[]> {
    const result = await http.post(
      'api/services/app/Inspection/CreateInspectionResponses',
      request,
      // The drawer reports saving, retrying and offline on its own, and this retries by itself on a
      // heartbeat - a toast per attempt would be noise on top of something already handled.
      { suppressErrorMessage: true }
    );
    return result.data.result;
  }

  /**
   * One photo, one request. onProgress reports bytes actually on the wire, so the caller can put a
   * real number on the thumbnail instead of an indefinite spinner.
   */
  public async uploadResponseDocument(
    request: UploadInspectionResponseDocument,
    options?: { onProgress?: (percent: number) => void; signal?: AbortSignal }
  ): Promise<DocumentDto> {
    const result = await http.post(
      'api/services/app/Inspection/UploadInspectionResponseDocument',
      request,
      {
        // A photo on a garage connection deserves more than the 30s the rest of the app runs on.
        timeout: 120000,
        // The thumbnail carries its own failed state and retry.
        suppressErrorMessage: true,
        signal: options?.signal,
        onUploadProgress: (event) => {
          if (!options?.onProgress || !event.total) return;
          options.onProgress(Math.round((event.loaded * 100) / event.total));
        },
      }
    );
    return result.data.result;
  }

  public async deleteResponseDocument(documentId: string): Promise<void> {
    await http.post('api/services/app/Inspection/DeleteInspectionResponseDocument', {
      id: documentId,
    });
  }

  public async createOrderInspection({
    categoryId,
    orderId,
  }: {
    categoryId: number;
    orderId: string;
  }): Promise<Inspection> {
    const result = await http.post('api/services/app/Inspection/CreateOrderInspection', {
      categoryId,
      orderId,
    });
    return result.data.result;
  }

  public async sendInspectionEmail(id: string, email?: string): Promise<void> {
    await http.post('api/services/app/Inspection/SendInspectionEmail', { id, email });
  }

  public async deleteOrderInspection({
    categoryId,
    orderId,
  }: {
    categoryId: number;
    orderId: string;
  }): Promise<string> {
    const result = await http.post('api/services/app/Inspection/DeleteOrderInspection', {
      categoryId,
      orderId,
    });
    return result.data.result;
  }
}

export default new InspectionService();
